<template>
    <validation-observer ref="form" v-slot="{ meta, handleSubmit, errors, setFieldValue }">
        <modal
            :id="modalId"
            :title="title"
            ok-title="Save"
            :ok-disabled="!meta.valid"
            scrollable
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            @shown="setData"
        >
            <busy-modal :show="busy"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)">
                <b-row>
                    <b-col>
                        <validation-provider name="name">
                            <form-input
                                label="Name:"
                                type="text"
                                v-model="values.name"
                                :error="errors.name"
                                @input="(val) => setFieldValue('name', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="description" rules="required">
                            <form-input
                                label="Description:"
                                type="text"
                                description='This is the title of the field. For example, if the variable is for the input of property notes, the title might be "Property Notes".'
                                v-model="values.description"
                                :error="errors.description"
                                @input="(val) => setFieldValue('description', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isInvoice">
                    <b-col>
                        <form-select
                            label="Type:"
                            description="Select input field type."
                            v-model="values.input_type"
                            :options="typeOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row v-if="isInputTypeText">
                    <b-col>
                        <validation-provider name="length" rules="validPositiveInteger">
                            <form-input
                                label="Length:"
                                description="Please enter the desired field length. Longer fields are required if you are entering more data."
                                v-model="values.length"
                                :error="errors.length"
                                @input="(val) => setFieldValue('length', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isInputTypeText">
                    <b-col>
                        <validation-provider name="rows" rules="validPositiveInteger">
                            <form-input
                                label="Rows:"
                                description="Please enter the desired number of rows. More rows will be required if you are entering multiple sentences, etc."
                                v-model="values.rows"
                                :error="errors.rows"
                                @input="(val) => setFieldValue('rows', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isInputTypeText">
                    <b-col>
                        <form-input
                            :label="isGlobal ? 'Value:' : 'Default Value:'"
                            type="text"
                            description="This field is specific to global variables only. The data entered into this custom field will be available to you when setting up custom templates or emails."
                            v-model="values.value"
                        ></form-input>
                    </b-col>
                </b-row>
                <b-row v-if="isInvoice">
                    <b-col>
                        <form-checkbox
                            v-model="values.is_collected_at_booking"
                            @change="handleChangeClick"
                            description="If checked, this custom field will be displayed in the booking form to be filled out by the guest at the time of booking."
                            >Filled out by the guest at the time of booking</form-checkbox
                        >
                    </b-col>
                </b-row>
                <b-row v-if="isInvoice">
                    <b-col>
                        <form-checkbox
                            v-model="values.is_compulsory_at_booking"
                            :disabled="!values.is_collected_at_booking"
                            description="If checked, this custom field will be required in the booking form to be filled out by the guest at the time of booking."
                            >Required at the time of booking</form-checkbox
                        >
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>
<script>
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import { formatResponseError } from '../../../utils/api';

    export default {
        name: 'VariablesDetailModalForm',
        components: {
            BusyModal,
            Modal,
            FormInput,
            ResponsiveButton,
            FormCheckbox,
            FormSelect,
        },
        props: {
            modalId: String,
            variableType: String,
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            create: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                busy: false,
                values: {},
                typeOptions: [
                    { value: 'text', text: 'Text' },
                    { value: 'date', text: 'Date' },
                ],
            };
        },
        mounted() {
            this.values = this.getInitialValues();
        },
        computed: {
            title() {
                const title = this.initialValues.id ? 'Edit ' : 'Create ';
                return title + this.variableType;
            },
            isGlobal() {
                return this.variableType === 'Global Variable';
            },
            isInvoice() {
                return this.variableType === 'Invoice Variable';
            },
            isInputTypeText() {
                return !!(this.values && this.values.input_type === 'text');
            },
        },
        methods: {
            getInitialValues() {
                return {
                    id: this.initialValues.id || null,
                    description: this.initialValues.description || '',
                    input_type: this.initialValues.input_type || 'text',
                    is_collected_at_booking: this.initialValues.is_collected_at_booking || false,
                    is_compulsory_at_booking: this.initialValues.is_compulsory_at_booking || false,
                    length: this.initialValues.length || 20,
                    name: this.initialValues.name || null,
                    order: this.initialValues.order || null,
                    rows: this.initialValues.rows || 1,
                    value: this.initialValues.value || '',
                };
            },
            setData() {
                this.$refs.form.setValues(this.values);
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            handleChangeClick() {
                if (this.values.is_collected_at_booking) {
                    this.values.is_compulsory_at_booking = false;
                }
            },
            async save() {
                this.$parent.$emit('busy', true);
                this.busy = true;

                try {
                    await (this.values.id ? this.update(this.values) : this.create(this.values));
                    this.$emit('success-save');
                    this.$nextTick(() => {
                        this.hideModal();
                    });
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not save variable.' });
                } finally {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>
